import { type MigrationFunction } from './MigrationFunction.ts';

/**
 * Classe astratta per l'implementazione di un sistema di gestione delle migrazioni
 */
export abstract class MigrationEngine<T> {
  readonly migrations: MigrationFunction<T>[];

  constructor(migrations: MigrationFunction<T>[]) {
    this.migrations = migrations;
    for (let i = 0; i < this.migrations.length; i++) {
      if (this.migrations[i].versionId !== i + 1) {
        throw new Error(
          `Le migrazioni devono essere ordinate e sequenziali. Migrazione #${i} ha ID ${this.migrations[i].versionId}`,
        );
      }
    }
  }

  /** Ritorna il version ID in base alla struttura dei dati in input */
  abstract getVersionId(data: T): number;

  /** Imposta un nuovo version ID in base alla struttura dei dati in input */
  abstract setVersionId(data: T, versionId: number): void;

  /** Ritorna l'attuale version ID del migrator, pari al numero di migrazioni presenti */
  get actualVersionId(): number {
    return this.migrations.length;
  }

  needMigration(data: T): boolean {
    const versionId = this.getVersionId(data);

    // Se la migrazione dei dati è uguale a quella attuale, allora non c'è nulla da fare
    if (versionId === this.actualVersionId) return false;

    // Dati salvati da una versione più recente di quella supportata dal codice
    // (build più vecchia, ambiente disallineato): non sono migrabili. Non
    // blocchiamo il caricamento con un errore secco — li leggiamo così come
    // sono, segnalando un errore.
    if (versionId > this.actualVersionId) {
      console.error(
        `[MigrationEngine] Versione #${versionId} non supportata. Versione attuale: #${this.actualVersionId}. I dati verranno letti senza migrazione.`,
      );
      return false;
    }

    return true;
  }

  /**
   * Esegue tutte le migrazioni necessarie per portare i dati alla versione attuale
   */
  run(data: T) {
    const versionId = this.getVersionId(data);

    if (!this.needMigration(data)) return;

    // Altrimenti, eseguo tutte le migrazioni rimanenti

    const isDev = process.env.NODE_ENV === 'development';
    const migrationsToRun = this.migrations.slice(versionId);

    if (isDev) {
      // biome-ignore format: <tbd>
      console.debug(`[MigrationEngine] Migrazione dati da versione #${versionId} a #${this.actualVersionId} (${migrationsToRun.length} step)`);
    }

    for (const [index, migration] of migrationsToRun.entries()) {
      const step = `ID#${index + 1}/${migrationsToRun.length}`;
      // Se una migrazione fallisce degradiamo con grazia: non scriviamo la
      // versione e proseguiamo leggendo i dati così come sono (best-effort).
      if (!this.runMigration(migration, data, step, isDev)) return;
    }

    // Infine, aggiorno il version ID
    this.setVersionId(data, this.actualVersionId);

    if (isDev) {
      // biome-ignore format: <tbd>
      console.debug(`[MigrationEngine] Migrazione completata: versione #${this.actualVersionId}`);
    }
  }

  /**
   * Esegue una singola migrazione, raccogliendone i log diagnostici.
   *
   * @returns `true` se la migrazione è andata a buon fine, `false` in caso di
   * errore (già loggato).
   */
  private runMigration(
    migration: MigrationFunction<T>,
    data: T,
    step: string,
    isDev: boolean,
  ): boolean {
    if (isDev) {
      // biome-ignore format: <tbd>
      console.debug(`[MigrationEngine] Esecuzione migrazione ${step}: ${migration.name}`);
    }
    // Le migrazioni raccolgono messaggi diagnostici via `this.log(...)`:
    // azzeriamo prima dell'esecuzione per avere solo i log di questo run.
    migration.logs.length = 0;
    try {
      migration.run(data);
    } catch (e) {
      // biome-ignore format: <tbd>
      console.error(`[MigrationEngine] Migrazione ${step} fallita: ${migration.name}`, e);
      if (migration.logs.length) {
        // biome-ignore format: <tbd>
        console.error(`[MigrationEngine] Log parziali di ${migration.name}:`, migration.logs);
      }
      // Il widget tree completo al momento del fallimento è utile per la
      // diagnosi: lo logghiamo sempre insieme all'errore.
      console.error('[MigrationEngine] Dati al momento del fallimento:', data);
      return false;
    }
    if (isDev && migration.logs.length) {
      // biome-ignore format: <tbd>
      console.debug(`[MigrationEngine] Log di ${migration.name}:`, migration.logs);
    }
    return true;
  }
}
